import * as yup from 'yup'

import {
  MAX_TASKS_PER_JOB,
  TASK_LABEL_MAX_LENGTH,
  TASK_WEIGHT_MAX,
  TASK_WEIGHT_MIN,
  TASK_WEIGHT_STEP,
} from '../utils/jobScope.functions'

export const PHONE_REGEX = new RegExp(
  /^(\+?\d{1,4}[\s.-]?)?(\(?\d{1,5}\)?[\s.-]?)?[\d\s.-]{5,}$/,
)
// Password complexity policy: >= 12 chars, with at least one uppercase, one
// lowercase, one digit, and one non-alphanumeric character. Any special
// character is accepted (not a fixed whitelist). Kept in sync with the API's
// PASSWORD_REGEX (api/src/models/schemas/auth.schemas.ts) — the two repos are
// separate, so the pattern is intentionally duplicated and must be updated in
// both places. The backend is the authoritative check; this copy is for UX.
export const PASSWORD_REGEX = new RegExp(
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z\d]).{12,}$/,
)

// Sections are polymorphic (XOR networkTrackId | projectId | buildingId) — mirrors
// the API's SectionDataSchema. Row-level completeness is enforced by SectionsForActivity
// via hasIncompleteRows, and by the backend on submit; the Yup schema stays permissive
// so edits of BUILDING_ACCESS (buildingId, null kms) and ENCLOSURE_ACCESS (projectId)
// activities don't get blocked by a track-only shape.
const sectionsFieldSchema = yup
  .array()
  .of(
    yup.object().shape({
      id: yup.string().optional(),
      projectId: yup.string().nullable().optional(),
      buildingId: yup.string().nullable().optional(),
      kmStart: yup.number().nullable().optional(),
      kmEnd: yup.number().nullable().optional(),
    }),
  )
  .optional()

// Job scope rows. A blank label is allowed here because the submit drops
// such rows silently; only the upper bound needs a form error.
const tasksFieldSchema = yup
  .array()
  .of(
    yup.object().shape({
      id: yup.string().optional(),
      label: yup.string().max(TASK_LABEL_MAX_LENGTH),
      weight: yup
        .number()
        .min(TASK_WEIGHT_MIN)
        .max(TASK_WEIGHT_MAX)
        .test(
          'half-step',
          'weight must be a multiple of 0.5',
          value =>
            value === undefined ||
            Math.abs(
              value / TASK_WEIGHT_STEP - Math.round(value / TASK_WEIGHT_STEP),
            ) < 1e-9,
        ),
    }),
  )
  .max(MAX_TASKS_PER_JOB)
  .optional()

export const newDemandSchema = yup.object().shape({
  requestorName: yup.string().required(),
  phoneNumber: yup.string().required(),
  // Legacy free-text holder — optional (resource holders replace it).
  holderName: yup.string().nullable().optional(),
  holderPhoneNumber: yup.string().nullable().optional(),
  resourceIds: yup.array().of(yup.string().required()).required().min(1),
  description: yup.string().required(),
  docNumber: yup.string().optional(),
  sections: sectionsFieldSchema,
  tasks: tasksFieldSchema,
  kmStart: yup.number().optional().nullable(),
  kmEnd: yup.number().optional().nullable(),
  kmEntrance: yup
    .string()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value,
    )
    .optional(),
  kmExit: yup
    .string()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value,
    )
    .optional(),
  entryTrackIds: yup.array().of(yup.string()).optional(),
  exitTrackIds: yup.array().of(yup.string()).optional(),
  teamId: yup.string().required(),
  company: yup.string(),
  comment: yup.string(),
  startTime: yup
    .string()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value,
    )
    .optional(),
  endTime: yup
    .string()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value,
    )
    .optional(),
  shift: yup.mixed<'day' | 'night'>().required().oneOf(['day', 'night']),
  workingDays: yup.array().of(yup.string()).required().min(1),
  zoneIds: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().required(),
        type: yup
          .string()
          .required()
          .oneOf([
            'sector',
            'subSector',
            'geoMarker',
            'protectionZone',
            'trainStation',
          ]),
      }),
    )
    .optional(),
})

export const modificationDemandSchema = yup.object().shape({
  requestorName: yup.string().required(),
  phoneNumber: yup.string().required(),
  // Legacy free-text holder — optional since Lot 2 (resource holders replace it).
  holderName: yup.string().nullable().optional(),
  holderPhoneNumber: yup.string().nullable().optional(),
  // Resource holders — at least one is required.
  resourceIds: yup.array().of(yup.string().required()).required().min(1),
  description: yup.string().required(),
  docNumber: yup.string(),
  sections: sectionsFieldSchema,
  tasks: tasksFieldSchema,
  kmStart: yup.number().optional().nullable(),
  kmEnd: yup.number().optional().nullable(),
  kmEntrance: yup
    .string()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value,
    )
    .optional(),
  kmExit: yup
    .string()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value,
    )
    .optional(),
  entryTrackIds: yup.array().of(yup.string()).optional(),
  exitTrackIds: yup.array().of(yup.string()).optional(),
  teamId: yup.string().required(),
  company: yup.string(),
  comment: yup.string(),
  startTime: yup
    .string()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value,
    )
    .optional(),
  endTime: yup
    .string()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value,
    )
    .optional(),
  shift: yup.mixed<'day' | 'night'>().required().oneOf(['day', 'night']),
  workingDays: yup.mixed<string | string[]>().required(),
  zoneIds: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().required(),
        type: yup
          .string()
          .required()
          .oneOf([
            'sector',
            'subSector',
            'geoMarker',
            'protectionZone',
            'trainStation',
          ]),
      }),
    )
    .optional(),
})

export const infoGlobalSchema = yup.object().shape({
  author: yup.string().required(),
  telAuthor: yup.string().required(),
  updateDate: yup.string().required(),
  informationText: yup.string().required(),
  secteurList: yup.array().of(
    yup.object().shape({
      name: yup.string().required(),
    }),
  ),
})

export const sectorFormSchema = yup.object().shape({
  id: yup.string().required(),
  name: yup.string().required(),
  acronym: yup.string().required(),
  kmStart: yup.number().required(),
  kmEnd: yup.number().required(),
})

export const teamFormSchema = yup.object().shape({
  name: yup.string().required(),
  acronym: yup.string().required(),
  color: yup.string().required(),
  isActive: yup.boolean().required(),
})

export const resourceFormSchema = yup.object().shape({
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  roleIds: yup.array().of(yup.string().required()).required().min(1),
  // phone and contact are optional (backend stores them nullable). Empty
  // strings are normalised to null. email is mandatory since v1.18.
  phone: yup
    .string()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value,
    )
    .optional(),
  contact: yup
    .string()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value,
    )
    .optional(),
  email: yup.string().email().required(),
  location: yup
    .string()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value,
    )
    .optional(),
  teamId: yup
    .string()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value,
    )
    .optional(),
  isActive: yup.boolean().required(),
})

export const resourceRoleFormSchema = yup.object().shape({
  name: yup.string().required(),
  color: yup.string().required(),
  icon: yup.string().required(),
  isActive: yup.boolean().required(),
})

export const loginSchema = yup.object().shape({
  email: yup.string().email().required(),
  password: yup.string().required(),
  rememberMe: yup.boolean().required(),
})

export const registerSchema = yup.object().shape({
  email: yup.string().email().required(),
  password: yup.string().required().matches(PASSWORD_REGEX).required(),
  passwordConfirmation: yup
    .string()
    .oneOf([yup.ref('password')])
    .required()
    .matches(PASSWORD_REGEX)
    .required(),
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  phone: yup.string().matches(PHONE_REGEX).required(),
  token: yup.string().required(),
})

export const passwordForgotSchema = yup.object().shape({
  email: yup.string().email().required(),
})

export const resetPasswordSchema = yup.object().shape({
  password: yup.string().required().matches(PASSWORD_REGEX).required(),
  passwordConfirmation: yup
    .string()
    .oneOf([yup.ref('password')])
    .required()
    .matches(PASSWORD_REGEX)
    .required(),
  token: yup.string().required(),
})

export const clientFormSchema = yup.object().shape({
  logo: yup.mixed<File>().optional(),
  name: yup.string().required(),
  email: yup.string().email().required(),
})
